import type { KnowledgeBase } from '@ralph/contracts'
import { ChevronDown, Search } from 'lucide-react'
import { useId, useState } from 'react'
import { Badge, Button, EmptyState, Input, Panel, Readout, Select, Spinner } from '@/components/ui/primitives.tsx'
import { useSearchKnowledgeBase } from '@/hooks/use-knowledge-bases.ts'
import { useI18n } from '@/i18n/index.ts'
import { asSentence, messageOf } from './knowledge-viewer.ts'

/**
 * Search one knowledge base the way an agent does (D-45): the same code path the agent's search
 * tool runs, under the reader's own access, so what comes back is what a model would have seen.
 *
 * Everyone who can use the base gets the passages and the documents they came from. Its
 * maintainers can also see how each passage was found — by matching its words, its meaning, or
 * both, and how the two rankings combined — behind "How it was ranked", because that is the
 * diagnosis of "why did it answer that" and nobody else needs it in the way.
 */

type Strategy = 'hybrid' | 'semantic' | 'lexical'

export const SearchTab = ({
  base,
  canMaintain,
  onOpenDocument
}: {
  base: Pick<KnowledgeBase, 'id' | 'name'>
  canMaintain: boolean
  onOpenDocument: (documentId: string) => void
}) => {
  // Inside the body, never at module scope: a capture taken at import freezes the language and
  // renders the previous one after a switch, with nothing failing (PRD-27 F-3, LOC-012).
  const fmt = useI18n()
  const t = fmt.t.knowledge.search
  const search = useSearchKnowledgeBase(base.id)
  const inputId = useId()
  const rankedId = useId()
  const strategyId = useId()
  const [query, setQuery] = useState('')
  const [strategy, setStrategy] = useState<Strategy>('hybrid')
  const [ranked, setRanked] = useState(false)
  const showRanks = canMaintain && ranked

  const run = () => {
    const text = query.trim()
    if (text === '' || search.isPending) return
    search.mutate({ query: text, limit: 10, strategy: canMaintain ? strategy : 'hybrid' })
  }

  return (
    <div className="space-y-3">
      <Panel className="p-3">
        <form
          aria-label={t.label}
          className="flex flex-wrap items-center gap-2"
          onSubmit={(event) => {
            event.preventDefault()
            run()
          }}
        >
          <label htmlFor={inputId} className="sr-only">
            {t.label}
          </label>
          <div className="min-w-[11rem] flex-1">
            <Input
              id={inputId}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t.placeholder}
            />
          </div>
          <Button
            type="submit"
            variant="primary"
            icon={search.isPending ? <Spinner size={12} /> : <Search size={13} aria-hidden />}
            disabled={search.isPending || query.trim() === ''}
          >
            {search.isPending ? t.running : t.run}
          </Button>
          {canMaintain && (
            <Button
              variant="ghost"
              aria-expanded={ranked}
              aria-controls={rankedId}
              trailing={
                <ChevronDown
                  size={13}
                  aria-hidden
                  className={ranked ? 'rotate-180 transition-transform' : 'transition-transform'}
                />
              }
              onClick={() => setRanked(!ranked)}
            >
              {t.ranked}
            </Button>
          )}
        </form>

        {canMaintain && ranked && (
          <div id={rankedId} className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-rule pt-3">
            <p className="min-w-[14rem] flex-1 text-pretty text-[12.5px] leading-[1.45] text-fg-muted">
              {t.rankedLead}
            </p>
            <span className="flex items-center gap-2 text-[12.5px] text-fg-muted">
              <label htmlFor={strategyId}>{t.strategyLabel}</label>
              <span className="w-[11.5rem]">
                <Select
                  id={strategyId}
                  value={strategy}
                  onChange={(event) => setStrategy(event.target.value as Strategy)}
                >
                  <option value="hybrid">{t.strategies.hybrid}</option>
                  <option value="semantic">{t.strategies.semantic}</option>
                  <option value="lexical">{t.strategies.lexical}</option>
                </Select>
              </span>
            </span>
          </div>
        )}

        {!search.data && !search.isPending && (
          <p className="mt-2.5 text-pretty text-[12.5px] leading-[1.45] text-fg-subtle">
            {canMaintain ? t.hintMaintainer : t.hint}
          </p>
        )}

        {search.data && showRanks && (
          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 border-t border-rule pt-3">
            <Readout label={t.readouts.time} value={`${search.data.latencyMs}ms`} />
            <Readout label={t.readouts.words} value={search.data.lexicalCandidates} />
            <Readout label={t.readouts.meaning} value={search.data.semanticCandidates} />
            <Readout label={t.readouts.returned} value={search.data.results.length} />
          </div>
        )}
      </Panel>

      {/* The count is announced; the passages are read at the reader's own pace. */}
      <p role="status" className="sr-only">
        {search.data ? t.results(fmt, search.data.results.length) : ''}
      </p>

      {search.data &&
        (search.data.results.length === 0 ? (
          <Panel>
            <EmptyState title={t.noResults} body={t.noResultsBody} />
          </Panel>
        ) : (
          <ol className="space-y-2.5">
            {search.data.results.map((result, index) => (
              <li key={result.chunkId}>
                <Panel className="px-4 py-3.5">
                  <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1.5">
                    <span className="readout font-medium text-primary">[{index + 1}]</span>
                    <button
                      type="button"
                      onClick={() => onOpenDocument(result.documentId)}
                      className="min-w-0 text-left text-[13.5px] font-medium tracking-[-0.014em] underline decoration-transparent underline-offset-2 transition-colors duration-200 hover:decoration-current"
                    >
                      {result.documentTitle}
                    </button>
                    <span className="readout text-fg-subtle">{result.locator}</span>
                    {showRanks && (
                      <span className="ml-auto flex flex-wrap items-center gap-1.5">
                        {result.lexicalRank !== null && <Badge>{t.byWords(result.lexicalRank)}</Badge>}
                        {result.semanticRank !== null && <Badge>{t.byMeaning(result.semanticRank)}</Badge>}
                        <Badge tone="grounded">{t.combined(result.score.toFixed(4))}</Badge>
                      </span>
                    )}
                  </div>
                  {(result.sectionPath ?? result.heading) && (
                    <p className="readout mt-1.5 text-fg-subtle">{result.sectionPath ?? result.heading}</p>
                  )}
                  <p className="mt-2 line-clamp-4 whitespace-pre-wrap text-[13px] leading-relaxed text-fg-muted">
                    {result.content}
                  </p>
                </Panel>
              </li>
            ))}
          </ol>
        ))}

      {search.isError && (
        <p role="alert" className="text-[13px] leading-[1.45] text-alert">
          {t.failed} {asSentence(messageOf(search.error, fmt.t.knowledge.genericError))}
        </p>
      )}
    </div>
  )
}
